import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthResponse, User } from '@lov/shared';
import { storage } from '../lib/utils/storage';
import { apiClient, setTokenExpiry, clearTokenExpiry, setLoggingOut } from '../lib/api/client';
import { onLogout } from '../lib/api/authEvents';

type SessionState = 'valid' | 'expired';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  sessionState: SessionState;
  login: (response: AuthResponse) => void;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Manages user session state
 *
 * IMPORTANT: Tokens are stored in httpOnly cookies (managed by backend).
 * This provider only manages user data in localStorage for UI personalization.
 * Authentication state is determined by the presence of user data + valid cookie session.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionState, setSessionState] = useState<SessionState>('valid');
  const queryClient = useQueryClient();

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = storage.getUser();

    if (storedUser) {
      setUser(storedUser);
    }

    setLoading(false);
  }, []);

  // Subscribe to auth event emitter — handles session expiry from API interceptor
  useEffect(() => {
    const unsub = onLogout(() => {
      storage.clearAuth();
      clearTokenExpiry();
      setUser(null);
      setSessionState('expired');
      // Prevent stale data from previous session showing after re-login
      queryClient.clear();
      // Preserve return-to URL for post-login redirect
      sessionStorage.setItem('lov_return_to', window.location.pathname + window.location.search);
      sessionStorage.setItem('lov_session_expired', 'true');
    });
    return unsub;
  }, [queryClient]);

  const login = (response: AuthResponse) => {
    const { user: userData } = response;

    // Store user in state and localStorage
    setUser(userData);
    storage.setUser(userData);
    setSessionState('valid');

    // Note: Tokens are already set in httpOnly cookies by the backend
    // Track token expiry in memory for proactive refresh
    setTokenExpiry();
  };

  const logout = async () => {
    // Signal to the API client that logout is in progress — prevents proactive
    // token refresh and 401 auto-refresh from re-authenticating the user.
    setLoggingOut(true);
    try {
      // Tell the backend to invalidate the session and clear httpOnly cookies.
      // Best-effort: even if the call fails we still clear local state below.
      await apiClient.post('/auth/logout', {});
    } catch {
      // Ignore errors — network issues shouldn't block a client-side logout
    } finally {
      // Always clear user state and localStorage regardless of API result
      setUser(null);
      storage.clearAuth();
      clearTokenExpiry();
      setLoggingOut(false);
      setSessionState('valid');
    }
  };

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
    storage.setUser(updatedUser);
  }, []);

  const clearSessionExpired = useCallback(() => {
    setSessionState('valid');
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user, // User presence indicates valid session (cookies validated by API)
    sessionState,
    login,
    logout,
    updateUser,
    clearSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
